import React from 'react';
import { Edit2, Star, TrendingUp, Palette } from 'lucide-react';

interface Child {
  id: string;
  name: string;
  age: number;
  avatar_color: string;
  total_points: number;
  created_at: string;
}

interface ChildCardProps {
  child: Child;
  onEdit: (child: Child) => void;
  onCustomizeTheme: (childId: string) => void;
}

export function ChildCard({ child, onEdit, onCustomizeTheme }: ChildCardProps) {
  const level = Math.floor(child.total_points / 100) + 1;
  const progressToNextLevel = child.total_points % 100;

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
      <div className="p-6">
        {/* Header with Avatar and Actions */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
              style={{ backgroundColor: child.avatar_color }}
            >
              {child.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 font-baloo">{child.name}</h3>
              <p className="text-sm text-gray-600">Age {child.age}</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => onCustomizeTheme(child.id)}
              className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              title="Customize theme"
            >
              <Palette className="w-4 h-4" />
            </button>
            <button
              onClick={() => onEdit(child)}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Edit child"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Stats and Progress */}
        <div className="space-y-4">
          {/* Points and Level */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-2">
              <Star className="w-4 h-4 text-yellow-500" />
              <span className="text-gray-700">{child.total_points} points</span>
            </div>
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-4 h-4 text-green-500" />
              <span className="text-gray-700">Level {level}</span>
            </div>
          </div>

          {/* Progress Bar */}
          <div>
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span>Progress to Level {level + 1}</span>
              <span>{progressToNextLevel}/100</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-purple-500 to-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressToNextLevel}%` }}
              />
            </div>
          </div>

          {/* Created Date */}
          <div className="text-xs text-gray-500">
            Created {new Date(child.created_at).toLocaleDateString()}
          </div>
        </div>
      </div>
    </div>
  );
}
